import type { PlayerInput } from '../sim/input';
import { worldToScreen, type CameraState } from '../render/camera';
import { keyHeld, keyMatches } from './bindings';
import { aimWeaponAt } from '../sim/weapons/geometry';
import type { WeaponDef } from '../sim/weapons/schema';

type MouseAimPose = { x: number; y: number; ducking?: boolean; height?: number; weapon?: Pick<WeaponDef, 'shape' | 'twoHanded'> };

export type KeyLatch = {
  jump: boolean;
  attack: boolean;
  block: boolean;
  throw: boolean;
  pause: boolean;
};

export function createKeyboardFallback(): {
  sample: (
    playerPos: MouseAimPose,
    cam: CameraState,
    viewW: number,
    viewH: number,
  ) => PlayerInput;
  consume: () => void;
  /** True once per Escape/P press, however brief; polling `down` would miss a tap shorter than a frame. */
  takePause: () => boolean;
  /** Forget physical holds and pending presses when input ownership changes. */
  clear: () => void;
  dispose: () => void;
  down: Set<string>;
  mouse: { x: number; y: number };
} {
  const down = new Set<string>();
  const mouse = { x: 0, y: 0 };
  let mouseActive = false;
  let lastMouseAim = { x: 0, y: 0 };
  const mouseDown = new Set<number>();
  const latch: KeyLatch = { jump: false, attack: false, block: false, throw: false, pause: false };
  const clear = () => {
    down.clear();
    mouseDown.clear();
    mouseActive = false;
    lastMouseAim = { x: 0, y: 0 };
    latch.jump = latch.attack = latch.block = latch.throw = latch.pause = false;
  };
  const editable = (target: EventTarget | null) =>
    typeof Element !== 'undefined' &&
    target instanceof Element &&
    !!target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])');
  const buttonTarget = (target: EventTarget | null) =>
    typeof Element !== 'undefined' && target instanceof Element &&
    !!target.closest('button, summary, [role="button"]');
  const onKeyDown = (e: KeyboardEvent) => {
    if (editable(e.target) || (buttonTarget(e.target) && (e.code === 'Space' || e.code === 'Enter'))) return;
    down.add(e.code);
    if (keyMatches('jump', e.code)) latch.jump = true;
    if (keyMatches('attack', e.code)) latch.attack = true;
    if (keyMatches('block', e.code)) latch.block = true;
    if (keyMatches('throw', e.code)) latch.throw = true;
    if (!e.repeat && keyMatches('pause', e.code)) latch.pause = true;
  };
  const onKeyUp = (e: KeyboardEvent) => down.delete(e.code);
  const onMouseDown = (e: MouseEvent) => {
    if (editable(e.target) || buttonTarget(e.target)) return;
    mouseActive = true;
    mouse.x = e.clientX;
    mouse.y = e.clientY;
    mouseDown.add(e.button);
    if (e.button === 0) latch.attack = true;
    if (e.button === 2) latch.block = true;
  };
  const onMouseUp = (e: MouseEvent) => mouseDown.delete(e.button);
  const onMouseMove = (e: MouseEvent) => {
    if (editable(e.target) || buttonTarget(e.target)) return;
    mouseActive = true;
    mouse.x = e.clientX;
    mouse.y = e.clientY;
  };
  const onContextMenu = (e: MouseEvent) => {
    if (!editable(e.target)) e.preventDefault();
  };
  const onFocus = (e: FocusEvent) => {
    if (editable(e.target) || buttonTarget(e.target)) clear();
  };
  const onVisibility = () => {
    if (document.hidden) clear();
  };
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('mousedown', onMouseDown);
  window.addEventListener('mouseup', onMouseUp);
  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('contextmenu', onContextMenu);
  window.addEventListener('blur', clear);
  window.addEventListener('focusin', onFocus);
  document.addEventListener('visibilitychange', onVisibility);

  return {
    down,
    mouse,
    clear,
    dispose() {
      clear();
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('blur', clear);
      window.removeEventListener('focusin', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    },
    sample(playerPos, cam, viewW, viewH) {
      const left = keyHeld('left', down);
      const right = keyHeld('right', down);
      const duck = keyHeld('down', down);
      const screen = worldToScreen(cam, playerPos.x, playerPos.y, viewW, viewH);
      const aimX = mouse.x - screen.x;
      const aimY = screen.y - mouse.y;
      const len = Math.hypot(aimX, aimY) || 1;
      const aimed = !mouseActive || len < 12 ? lastMouseAim : playerPos.weapon ? aimWeaponAt(
        { ...playerPos, ducking: playerPos.ducking ?? false },
        playerPos.weapon,
        { x: playerPos.x + aimX / cam.zoom, y: playerPos.y + aimY / cam.zoom },
      ) : { x: aimX / len, y: aimY / len };
      lastMouseAim = aimed;
      return {
        moveX: (right ? 1 : 0) - (left ? 1 : 0),
        jump: latch.jump || keyHeld('jump', down),
        down: duck,
        attack: latch.attack || mouseDown.has(0) || keyHeld('attack', down),
        block: latch.block || mouseDown.has(2) || keyHeld('block', down),
        throw: latch.throw || keyHeld('throw', down),
        aimX: aimed.x,
        aimY: aimed.y,
      };
    },
    consume() {
      latch.jump = false;
      latch.attack = false;
      latch.block = false;
      latch.throw = false;
    },
    takePause() {
      const p = latch.pause;
      latch.pause = false;
      return p;
    },
  };
}
